import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { api, errText, mediaUrl } from "@/lib/api";

const field = "w-full border border-black/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0A0A0A]";

const ImageBlock = ({ b, set, i }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose a photo");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/media", fd);
      set({ media_id: r.data?.id });
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      {b.media_id ? <img src={mediaUrl(b.media_id)} alt="" className="max-h-64 w-full object-contain bg-[#F3F2EF]" data-testid={`block-${i}-image-preview`} /> : null}
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`block-${i}-image-upload`}
        className="flex w-full items-center justify-center gap-2 border border-dashed border-black/30 py-5 text-sm font-semibold hover:bg-[#F3F2EF]">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}{b.media_id ? "Change picture" : "Upload a picture"}
      </button>
      <input ref={ref} type="file" accept="image/*" hidden onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
      <input value={b.caption} onChange={(e) => set({ caption: e.target.value })} placeholder="Caption (optional)" className={field} data-testid={`block-${i}-caption`} />
    </div>
  );
};

const LABELS = { text: "Text", image: "Picture", button: "Button link" };

export const BlockEditor = ({ b, i, count, set, move, remove }) => (
  <div className="border border-black/15 bg-white p-4" data-testid={`block-${i}`}>
    <div className="mb-3 flex items-center justify-between">
      <span className="font-mono-label text-xs uppercase tracking-[0.2em] text-[#57534E]">{LABELS[b.type]}</span>
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => move(-1)} disabled={i === 0} aria-label="Move up" className="rounded p-1.5 hover:bg-black/10 disabled:opacity-30" data-testid={`block-${i}-up`}><ArrowUp className="h-4 w-4" /></button>
        <button type="button" onClick={() => move(1)} disabled={i === count - 1} aria-label="Move down" className="rounded p-1.5 hover:bg-black/10 disabled:opacity-30" data-testid={`block-${i}-down`}><ArrowDown className="h-4 w-4" /></button>
        <button type="button" onClick={remove} aria-label="Remove" className="rounded p-1.5 hover:bg-black/10" data-testid={`block-${i}-remove`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
    {b.type === "text" && (
      <>
        <textarea value={b.text} onChange={(e) => set({ text: e.target.value })} rows={6} placeholder="Write your message…" className={field} data-testid={`block-${i}-text`} />
        <p className="mt-1 text-xs text-[#57534E]">Leave an empty line between paragraphs. Wrap words in **double stars** to make them bold.</p>
      </>
    )}
    {b.type === "image" && <ImageBlock b={b} i={i} set={set} />}
    {b.type === "button" && (
      <div className="grid gap-2 sm:grid-cols-2">
        <input value={b.label} onChange={(e) => set({ label: e.target.value })} placeholder="Button text, e.g. Watch now" className={field} data-testid={`block-${i}-label`} />
        <input value={b.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://…" className={field} data-testid={`block-${i}-url`} />
      </div>
    )}
  </div>
);
